/**
 * Projects (07 Workshop, rows 4, 14 and 48): every project as a tile, in the
 * person's order (the sidebar's sort), Active and Archived. A tile says what
 * the project is for, who can see it, what is inside and when it last moved;
 * its ⋯ holds Edit, Share, Archive and Delete. Search filters what is loaded.
 */
import { useMemo, useState } from "react";
import { Button, Menu, MenuItem, MenuTrigger, Popover, type Key } from "react-aria-components";
import { Check, FolderOpen, Lock, Plus, Users, WifiOff } from "lucide-react";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitSegmentedControl } from "@/app/routes/dashboard/components/kit/SegmentedControl";
import { WallSearch } from "@/app/routes/dashboard/components/WallBar";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { WorkshopProject } from "@/types/workshop.type";
import { useWorkshopProjects } from "../api/useWorkshopProjects";
import { useProjectsOutputs } from "../workshopOutputs";
import { PROJECT_SORTS, sortProjects, useProjectSort } from "../workshopSort";
import WorkshopProjectMenu from "./WorkshopProjectMenu";
import { useProjectActions } from "./WorkshopProjectActions";
import { RetryButton, WsStateBlock, isArchived, isNetworkError } from "./WorkshopParts";
import { useWorkshopDesk } from "./workshopDesk";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "SEP 24" (the year only when it is not this one). */
export function tileDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date
    .toLocaleDateString(undefined, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) })
    .toUpperCase();
}

/** Who can see a project: "Only you", or how many people are in it. */
export function projectWho(project: WorkshopProject): { label: string; shared: boolean } {
  const people = 1 + (project.members?.length ?? 0);
  if (project.isOwner !== false && people === 1) return { label: "Only you", shared: false };
  return { label: plural(people, "person", "people"), shared: true };
}

export default function WorkshopProjectsPage() {
  const desk = useWorkshopDesk();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const portal = useCoShellPortal();
  const { projects, isLoading, isError, error, refetch } = useWorkshopProjects(desk.brandId);
  const [sort, setSort] = useProjectSort();
  const [list, setList] = useState<"active" | "archived">("active");
  const [query, setQuery] = useState("");
  const { run, dialogs } = useProjectActions({
    brandId: desk.brandId,
    onProjectDeleted: (id) => {
      if (id === desk.activeProjectId) desk.openProjects();
    },
  });

  const activeProjects = projects.filter((p) => !isArchived(p));
  const archivedProjects = projects.filter(isArchived);
  const ids = useMemo(
    () => (list === "archived" ? archivedProjects : activeProjects).map((p) => p.id),
    // The id list is what the reads depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, projects.map((p) => `${p.id}:${p.status}`).join("|")],
  );
  const outputs = useProjectsOutputs(ids);
  const outputCount = (projectId: string): number | null => {
    const state = outputs.byProject.get(projectId);
    return state && state.ready && !state.failed ? state.outputs.length : null;
  };

  const q = query.trim().toLowerCase();
  const visible = sortProjects(list === "archived" ? archivedProjects : activeProjects, sort).filter(
    (p) => !q || p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q),
  );
  const phrase = PROJECT_SORTS.find((s) => s.id === sort)?.phrase ?? "by last activity";
  const listFailed = isError && projects.length === 0;

  return (
    <div className="ws-page" data-layout={isPhone ? "phone" : "desk"}>
      <header className="ws-page-head">
        <div className="ws-page-title-row">
          <h1 className="ws-page-title ws-page-title--xl">Projects</h1>
          <div className="ws-page-actions">
            {isPhone ? null : (
              <WallSearch value={query} onChange={setQuery} onSubmit={() => {}} label="Search projects" />
            )}
            {isPhone ? null : (
              <MenuTrigger>
                <KitButtonTrigger label={PROJECT_SORTS.find((s) => s.id === sort)?.label ?? ""} />
                <Popover
                  placement="bottom end"
                  offset={6}
                  UNSTABLE_portalContainer={portal ?? undefined}
                  className="ws-menu-pop ws-menu-pop--sort"
                >
                  <Menu
                    aria-label="Sort projects"
                    className="ws-menu-list"
                    selectionMode="single"
                    selectedKeys={[sort]}
                    onAction={(key: Key) => setSort(key as typeof sort)}
                  >
                    {PROJECT_SORTS.map((s) => (
                      <MenuItem key={s.id} id={s.id} textValue={s.label} className="ws-menu-row">
                        {({ isSelected }) => (
                          <>
                            <span className="ws-menu-row-label">{s.label}</span>
                            {isSelected ? <Check className="ws-menu-row-check" aria-hidden="true" /> : null}
                          </>
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                </Popover>
              </MenuTrigger>
            )}
            {isPhone ? null : (
              <KitButton iconLeft={Plus} onClick={() => desk.openNewProject((id) => desk.openProject(id))}>
                New project
              </KitButton>
            )}
          </div>
        </div>
        <p className="ws-page-sub">
          {desk.brandName ? `Every ${desk.brandName} project, ${phrase}.` : `Every project, ${phrase}.`} Each
          keeps its own chats and files.
        </p>
      </header>

      <div className="ws-page-body">
        {listFailed ? (
          isNetworkError(error) ? (
            <WsStateBlock
              icon={WifiOff}
              tone="alert"
              title="Can't reach Mohtawa"
              line="Your connection dropped or the server did not answer. Your projects are safe."
              action={<RetryButton onRetry={() => void refetch()} />}
            />
          ) : (
            <WsStateBlock
              icon={FolderOpen}
              tone="alert"
              title="Couldn't load your projects"
              line="The server did not answer. Nothing was lost."
              action={<RetryButton onRetry={() => void refetch()} />}
            />
          )
        ) : (
          <>
            <KitSegmentedControl
              variant="lens"
              ariaLabel="Active or archived"
              className="ws-page-seg"
              value={list}
              onChange={setList}
              options={[
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
              renderOption={(option) =>
                isLoading ? null : (
                  <span className="ws-seg-count">
                    {option.value === "active" ? activeProjects.length : archivedProjects.length}
                  </span>
                )
              }
            />
            {isLoading ? (
              <ul className="ws-tiles" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="ws-tile ws-tile--skel">
                    <span className="ws-skel ws-skel--title" />
                    <span className="ws-skel ws-skel--line" style={{ width: "80%" }} />
                    <span className="ws-skel ws-skel--line" style={{ width: "55%" }} />
                  </li>
                ))}
              </ul>
            ) : visible.length === 0 ? (
              <p className="ws-page-empty">
                {q
                  ? `No project matches “${query.trim()}”.`
                  : list === "archived"
                    ? "Nothing is archived."
                    : "No projects yet. A project holds the chats and files for one piece of work."}
              </p>
            ) : (
              <ul className="ws-tiles">
                {visible.map((project) => (
                  <ProjectTile
                    key={project.id}
                    project={project}
                    outputs={outputCount(project.id)}
                    onOpen={() => desk.openProject(project.id)}
                    menu={
                      <WorkshopProjectMenu
                        project={project}
                        canDelete={project.isOwner !== false}
                        asSheet={isPhone}
                        sheetMeta={tileMeta(project, outputCount(project.id)).toUpperCase()}
                        onAction={(action) => run(action, project)}
                        className="ws-tile-more"
                      />
                    }
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {isPhone ? (
        <KitButton
          size="lg"
          iconLeft={Plus}
          className="ws-page-fab"
          onClick={() => desk.openNewProject((id) => desk.openProject(id))}
        >
          New project
        </KitButton>
      ) : null}
      {dialogs}
    </div>
  );
}

/** The sort button looks like a Secondary button and opens the sort menu. */
function KitButtonTrigger({ label }: { label: string }) {
  return (
    <Button className="ws-secondary" aria-label={`Sort: ${label}`}>
      {label}
    </Button>
  );
}

/** "3 chats · 8 outputs" (outputs only once read). */
function inside(project: WorkshopProject, outputs: number | null): string {
  const parts = [plural(project.sessionCount ?? 0, "chat", "chats")];
  if (outputs != null) parts.push(plural(outputs, "output", "outputs"));
  return parts.join(" · ");
}

function tileMeta(project: WorkshopProject, outputs: number | null): string {
  return `${inside(project, outputs)} · ${projectWho(project).label}`;
}

function ProjectTile({
  project,
  outputs,
  onOpen,
  menu,
}: {
  project: WorkshopProject;
  outputs: number | null;
  onOpen: () => void;
  menu: React.ReactNode;
}) {
  const archived = isArchived(project);
  const who = projectWho(project);
  const WhoIcon = who.shared ? Users : Lock;
  return (
    <li className="ws-tile" data-archived={archived || undefined}>
      <button type="button" className="ws-tile-open" onClick={onOpen} aria-label={`Open ${project.name}`} />
      <div className="ws-tile-top">
        <h2 className="ws-tile-title">{project.name}</h2>
        {menu}
      </div>
      <p className="ws-tile-desc">
        {archived
          ? "Archived. Its chats and outputs stay; restore it from the ⋯ to work in it again."
          : project.description}
      </p>
      <span className="ws-tile-push" />
      <div className="ws-tile-foot">
        <WhoIcon className="ws-tile-who-icon" aria-hidden="true" />
        <span className="ws-tile-who">{who.label}</span>
        <span className="ws-tile-sep" aria-hidden="true">
          ·
        </span>
        <span className="ws-tile-who">{inside(project, outputs)}</span>
        <span className="ws-side-push" />
        <span className="ws-tile-when">
          {archived ? `ARCHIVED ${tileDate(project.updatedAt)}` : tileDate(project.updatedAt)}
        </span>
      </div>
    </li>
  );
}
